import axios from "axios";
import type { ApiError, ApiResponse } from "./deal-builder-api.ts";

// @ts-ignore
const DATABASE_API_URL = process.env.REACT_APP_API_URL;

// Extracting a large PDF with an LLM takes a while, so this request
// needs a much longer timeout than the rest of the API calls.
const EXTRACTION_TIMEOUT_MS = 180_000;

// Text, date and enum fields use an empty string as the "not stated"
// sentinel (the extraction schema can only afford a few real nullable
// fields); numeric and boolean fields use null.
export type ExtractedScheduleRow = {
    maturity_date: string,               // "YYYY-MM-DD"
    principal: number | null,            // dollars
    coupon: number | null,               // decimal fraction (0.05 = 5%)
    term_bond_maturity: string,          // shared by the sinking fund rows of the same term bond, "" for serial rows
};

export type ExtractedDeal = {
    borrower: string,
    issuance_title: string,
    delivery_date: string,
    first_coupon_date: string,
    call_date: string,
    state_of_issuance: string,
    security_type: string,
    rating_assumption: string,
    tax_designation: string,
    sale_method: string,
    state_enhancement: string,
    bond_insurance: boolean | null,
    denomination: number | null,
    par_amount: number | null,
    principal_frequency: 0 | 1 | 2,      // 0 = not stated
};

export type PdfExtractionResponse = {
    deal: ExtractedDeal,
    schedule: ExtractedScheduleRow[] | null,
    warnings: string[],
};

const GENERIC_EXTRACTION_ERROR: ApiError = {
    error: "generic-error",
    message: "We couldn't extract data from your PDF.",
    detail: undefined,
};

export default class PdfExtractionAPI {

    public static async extractPdf(token: string, file: File): Promise<ApiResponse<PdfExtractionResponse>> {
        const path = DATABASE_API_URL + "/v3/deals/extract-pdf";
        try {
            const form = new FormData();
            form.append("file", file, file.name);
            const config = {
                headers: { Authorization: `Bearer ${token}` },
                timeout: EXTRACTION_TIMEOUT_MS,
            };
            const { data } = await axios.post(path, form, config);
            return data;
        } catch (e) {
            if (axios.isAxiosError(e) && e.response?.data?.message) {
                return { error: e.response.data.error, message: e.response.data.message, detail: undefined };
            }
            return GENERIC_EXTRACTION_ERROR;
        }
    }

}
